// src/scenes/OptionsScene.js
// Options overlay — launched on top of TitleScene or MapScene.
// Controls: Music Shuffle toggle, Music Volume slider, SFX Volume slider.

import Phaser from "phaser";
import { Settings } from "../game/Settings.js";
import { I18n } from "../game/I18n.js";

const GOLD  = 0xfbbf24;
const PANEL_COLOR = 0x0d1525;
const TILE_STYLES = ['classic', 'prism', 'themed'];

export default class OptionsScene extends Phaser.Scene {
  constructor() { super("OptionsScene"); }

  create() {
    const W = this.scale.width;   // 540
    const H = this.scale.height;  // 960

    // ── Dim overlay (blocks taps to scene below except designated close zone) ──
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
      .setDepth(0)
      .setInteractive(); // swallow clicks

    // ── Panel ────────────────────────────────────────────────────────────────
    const PW = 460, PH = 560;
    const PX = W / 2;
    const PY = H / 2;
    const panelTop  = PY - PH / 2;
    const panelLeft = PX - PW / 2;

    const panelGfx = this.add.graphics().setDepth(1);
    panelGfx.fillStyle(PANEL_COLOR, 0.98);
    panelGfx.fillRoundedRect(panelLeft, panelTop, PW, PH, 14);
    panelGfx.lineStyle(1.5, GOLD, 0.5);
    panelGfx.strokeRoundedRect(panelLeft, panelTop, PW, PH, 14);

    // ── Title ────────────────────────────────────────────────────────────────
    this.add.text(PX, panelTop + 36, "OPÇÕES", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "34px",
      color: "#fbbf24",
    }).setOrigin(0.5).setDepth(2);

    // Divider
    this.add.rectangle(PX, panelTop + 66, PW - 40, 1, GOLD, 0.28).setDepth(2);

    // ── Row 1: Music Shuffle ─────────────────────────────────────────────────
    const ROW1_Y = panelTop + 122;
    this.add.text(panelLeft + 30, ROW1_Y, "Música Aleatória", {
      fontFamily: "system-ui, Arial",
      fontSize: "15px",
      color: "#cbd5e1",
    }).setOrigin(0, 0.5).setDepth(2);

    this._makeToggle(panelLeft + PW - 30 - 70, ROW1_Y, Settings.musicShuffle, (v) => {
      Settings.setMusicShuffle(v);
    });

    // Subtitle hint
    this.add.text(panelLeft + 30, ROW1_Y + 20, "toca músicas aleatórias em vez das regionais", {
      fontFamily: "system-ui, Arial",
      fontSize: "11px",
      color: "#475569",
      fontStyle: "italic",
    }).setOrigin(0, 0.5).setDepth(2);

    // ── Divider ──────────────────────────────────────────────────────────────
    this.add.rectangle(PX, panelTop + 178, PW - 40, 1, 0x1e293b, 1).setDepth(2);

    // ── Row 2: Music Volume ──────────────────────────────────────────────────
    const ROW2_Y = panelTop + 230;
    this.add.text(panelLeft + 30, ROW2_Y - 22, "Volume Música", {
      fontFamily: "system-ui, Arial",
      fontSize: "15px",
      color: "#cbd5e1",
    }).setOrigin(0, 0.5).setDepth(2);

    const SLIDER_W = PW - 60;
    this._makeSlider(panelLeft + 30, ROW2_Y + 8, SLIDER_W, Settings.musicVolume, (v) => {
      Settings.setMusicVolume(v);
      this._applyMusicVolume(v);
    });

    // ── Divider ──────────────────────────────────────────────────────────────
    this.add.rectangle(PX, panelTop + 308, PW - 40, 1, 0x1e293b, 1).setDepth(2);

    // ── Row 3: SFX Volume ────────────────────────────────────────────────────
    const ROW3_Y = panelTop + 360;
    this.add.text(panelLeft + 30, ROW3_Y - 22, "Volume Efeitos Sonoros", {
      fontFamily: "system-ui, Arial",
      fontSize: "15px",
      color: "#cbd5e1",
    }).setOrigin(0, 0.5).setDepth(2);

    this._makeSlider(panelLeft + 30, ROW3_Y + 8, SLIDER_W, Settings.sfxVolume, (v) => {
      Settings.setSfxVolume(v);
    });

    // ── Divider ──────────────────────────────────────────────────────────────
    this.add.rectangle(PX, panelTop + 418, PW - 40, 1, 0x1e293b, 1).setDepth(2);

    // ── Row 4: Tile Style ──────────────────────────────────────────────────
    const ROW4_Y = panelTop + 468;
    this.add.text(panelLeft + 30, ROW4_Y - 22, I18n.t('opt_tile_style'), {
      fontFamily: "system-ui, Arial",
      fontSize: "15px",
      color: "#cbd5e1",
    }).setOrigin(0, 0.5).setDepth(2);

    this._makeTileStyleSelector(panelLeft + 30, ROW4_Y + 8, SLIDER_W);

    // ── Close button ─────────────────────────────────────────────────────────
    const closeBtn = this.add.text(panelLeft + PW - 18, panelTop + 18, "✕", {
      fontFamily: "system-ui, Arial",
      fontSize: "22px",
      color: "#64748b",
    }).setOrigin(0.5).setDepth(3)
      .setInteractive({ useHandCursor: true });

    closeBtn.on("pointerover", () => closeBtn.setColor("#e2e8f0"));
    closeBtn.on("pointerout",  () => closeBtn.setColor("#64748b"));
    closeBtn.on("pointerup",   () => this._close());

    // Tap outside panel to close — delayed so the opening click doesn't
    // immediately trigger a close on the same pointerup event.
    this.time.delayedCall(250, () => {
      if (!this.scene.isActive()) return;
      const outsideHit = this.add.rectangle(W / 2, H / 2, W, H, 0xffffff, 0)
        .setDepth(0.5)
        .setInteractive();
      outsideHit.on("pointerup", (ptr) => {
        const inPanel = ptr.x >= panelLeft && ptr.x <= panelLeft + PW
                     && ptr.y >= panelTop  && ptr.y <= panelTop + PH;
        if (!inPanel) this._close();
      });
    });

    // ── Fade in ──────────────────────────────────────────────────────────────
    this.cameras.main.setAlpha(0);
    this.tweens.add({ targets: this.cameras.main, alpha: 1, duration: 180 });
  }

  // ── Toggle pill ────────────────────────────────────────────────────────────
  _makeToggle(x, y, initial, onChange) {
    const TW = 70, TH = 32;
    let on = initial;

    const gfx = this.add.graphics().setDepth(2);
    const label = this.add.text(x + TW / 2, y + TH / 2 + 11, "", {
      fontFamily: "system-ui, Arial", fontSize: "11px",
    }).setOrigin(0.5, 0).setDepth(2);

    const draw = () => {
      gfx.clear();
      gfx.fillStyle(on ? 0x22c55e : 0x334155, 1);
      gfx.fillRoundedRect(x, y - TH / 2, TW, TH, TH / 2);
      gfx.fillStyle(0xffffff, 1);
      gfx.fillCircle(on ? x + TW - TH / 2 : x + TH / 2, y, TH / 2 - 3);
      label.setText(on ? "ON" : "OFF").setColor(on ? "#22c55e" : "#64748b");
    };
    draw();

    this.add.rectangle(x + TW / 2, y, TW, TH, 0xffffff, 0)
      .setOrigin(0.5).setDepth(3)
      .setInteractive({ useHandCursor: true })
      .on("pointerup", () => { on = !on; draw(); onChange(on); });
  }

  // ── Drag slider ───────────────────────────────────────────────────────────
  _makeSlider(x, y, width, value, onChange) {
    const TRACK_H = 6;
    const HANDLE_R = 12;

    this.add.rectangle(x, y, width, TRACK_H, 0x1e293b, 1)
      .setOrigin(0, 0.5).setDepth(2);

    const fill = this.add.rectangle(x, y, Math.max(2, width * value), TRACK_H, GOLD, 1)
      .setOrigin(0, 0.5).setDepth(2);

    const handle = this.add.circle(x + width * value, y, HANDLE_R, GOLD, 1)
      .setDepth(3)
      .setInteractive({ draggable: true, useHandCursor: true });

    this.input.setDraggable(handle);
    handle.on("drag", (_ptr, dragX) => {
      const nx = Phaser.Math.Clamp(dragX, x, x + width);
      handle.x = nx;
      fill.width = Math.max(2, nx - x);
      onChange((nx - x) / width);
    });
  }

  // ── Apply music volume to any active bgm ──────────────────────────────────
  _applyMusicVolume(v) {
    for (const key of ['TitleScene', 'MapScene', 'LevelScene']) {
      const s = this.scene.get(key);
      if (s?.bgm?.isPlaying) s.bgm.setVolume(v);
    }
  }

  // ── Tile style 3-option selector ────────────────────────────────────────
  _makeTileStyleSelector(x, y, width) {
    const current = Settings.tileStyle;
    const btnW = Math.floor((width - 16) / 3);
    const btnH = 34;
    const gap  = 8;
    const btns = [];

    TILE_STYLES.forEach((style, i) => {
      const bx = x + i * (btnW + gap);
      const label = I18n.t(`opt_tile_${style}`);
      const isActive = style === current;

      // Button background
      const bg = this.add.graphics().setDepth(2);
      const drawBg = (active) => {
        bg.clear();
        bg.fillStyle(active ? GOLD : 0x1e293b, active ? 1 : 0.8);
        bg.fillRoundedRect(bx, y, btnW, btnH, 6);
        if (!active) {
          bg.lineStyle(1, 0x334155, 0.6);
          bg.strokeRoundedRect(bx, y, btnW, btnH, 6);
        }
      };
      drawBg(isActive);

      // Label
      const txt = this.add.text(bx + btnW / 2, y + btnH / 2, label, {
        fontFamily: "system-ui, Arial",
        fontSize: "12px",
        color: isActive ? "#0d1525" : "#94a3b8",
        fontStyle: "bold",
      }).setOrigin(0.5).setDepth(3);

      // Hit zone
      const hit = this.add.rectangle(bx + btnW / 2, y + btnH / 2, btnW, btnH, 0xffffff, 0)
        .setDepth(4)
        .setInteractive({ useHandCursor: true });

      btns.push({ style, bg, txt, hit, drawBg });

      hit.on("pointerup", () => {
        Settings.setTileStyle(style);
        // Update all buttons visually
        btns.forEach(b => {
          const active = b.style === style;
          b.drawBg(active);
          b.txt.setColor(active ? "#0d1525" : "#94a3b8");
        });
      });
    });
  }

  _close() {
    this.tweens.add({
      targets: this.cameras.main, alpha: 0, duration: 150,
      onComplete: () => this.scene.stop(),
    });
  }
}
